import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useStoreCart } from '../context/StoreCartContext';
import { useNotification } from '../components/Notification';
import { GoldenAmount } from '../components/store/StoreUi';

const EMPTY_ADDRESS = {
  fullName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  deliveryNotes: '',
};

function newCheckoutKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const CHECKOUT_KEY_STORAGE = 'wergame-store-checkout-key';

function persistedCheckoutKey() {
  const existing = sessionStorage.getItem(CHECKOUT_KEY_STORAGE);
  if (existing) return existing;
  const created = newCheckoutKey();
  sessionStorage.setItem(CHECKOUT_KEY_STORAGE, created);
  return created;
}

export default function StoreCheckout() {
  const { user, refreshUser } = useAuth();
  const { items, itemCount, totalGolden, clearCart } = useStoreCart();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const keyRef = useRef(persistedCheckoutKey());
  const [address, setAddress] = useState({
    ...EMPTY_ADDRESS,
    fullName: user?.username || '',
    email: user?.email || '',
  });
  const [customerNote, setCustomerNote] = useState('');
  const [balance, setBalance] = useState(user?.goldenTickets ?? 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get('/tickets/balances')
      .then(({ data }) => setBalance(data?.goldenTickets ?? user?.goldenTickets ?? 0))
      .catch(() => setBalance(user?.goldenTickets ?? 0));
  }, [user]);

  const shortfall = Math.max(0, totalGolden - balance);
  const requiredComplete = useMemo(
    () =>
      ['fullName', 'email', 'phone', 'addressLine1', 'city', 'state', 'postalCode', 'country'].every(
        (key) => String(address[key] || '').trim()
      ),
    [address]
  );

  const setField = (key, value) => setAddress((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!items.length || shortfall > 0 || !requiredComplete || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/store/checkout', {
        items: items.map((item) => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
        })),
        shippingAddress: address,
        customerNote,
        idempotencyKey: keyRef.current,
      });
      clearCart();
      sessionStorage.removeItem(CHECKOUT_KEY_STORAGE);
      await refreshUser();
      showNotification('Order confirmed! Your golden tickets were deducted.', 'success', 5000);
      navigate(`/store/orders/${data.order._id}`, { replace: true, state: { justPlaced: true } });
    } catch (err) {
      const data = err?.response?.data;
      if (typeof data?.balance === 'number') setBalance(data.balance);
      setError(data?.message || 'Checkout could not be completed. Please review your cart and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!items.length) {
    return (
      <main className="min-h-screen bg-slate-50 dark:bg-[#090d16] px-4 py-20 text-center">
        <p className="text-6xl" aria-hidden>🛒</p>
        <h1 className="mt-4 text-2xl font-black text-slate-900 dark:text-white">Your cart is empty</h1>
        <Link to="/store" className="mt-6 inline-flex rounded-xl bg-amber-400 px-6 py-3 font-black text-slate-950">
          Visit the store
        </Link>
      </main>
    );
  }

  const inputClass =
    'w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-3.5 py-3 text-sm text-slate-900 dark:text-white outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20';

  return (
    <main className="min-h-screen bg-slate-50 dark:bg-[#090d16] py-8 sm:py-10">
      <form onSubmit={submit} className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-7">
          <Link to="/store/cart" className="text-sm font-semibold text-amber-700 dark:text-amber-400 hover:underline">← Back to cart</Link>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black text-slate-950 dark:text-white">Secure checkout</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Your golden tickets are deducted only after stock and payment are confirmed.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5 sm:p-7">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15 font-black text-amber-800 dark:text-amber-300">1</span>
              <div>
                <h2 className="text-lg font-black text-slate-900 dark:text-white">Delivery details</h2>
                <p className="text-xs text-slate-500">Use an address where someone can receive your order.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ['fullName', 'Full name', 'text'],
                ['email', 'Email', 'email'],
                ['phone', 'Phone number', 'tel'],
                ['addressLine1', 'Address line 1', 'text'],
                ['addressLine2', 'Address line 2 (optional)', 'text'],
                ['city', 'City', 'text'],
                ['state', 'State / region', 'text'],
                ['postalCode', 'Postal / ZIP code', 'text'],
                ['country', 'Country', 'text'],
              ].map(([key, label, type]) => (
                <label key={key} className={key === 'addressLine1' || key === 'addressLine2' ? 'sm:col-span-2' : ''}>
                  <span className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">{label}</span>
                  <input
                    type={type}
                    required={!label.includes('optional')}
                    value={address[key]}
                    onChange={(event) => setField(key, event.target.value)}
                    className={inputClass}
                  />
                </label>
              ))}
              <label className="sm:col-span-2">
                <span className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">Delivery notes (optional)</span>
                <textarea
                  rows={3}
                  value={address.deliveryNotes}
                  onChange={(event) => setField('deliveryNotes', event.target.value)}
                  className={inputClass}
                  placeholder="Gate code, landmark, or delivery instructions"
                />
              </label>
              <label className="sm:col-span-2">
                <span className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">Order note (optional)</span>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={customerNote}
                  onChange={(event) => setCustomerNote(event.target.value)}
                  className={inputClass}
                  placeholder="Anything the store team should know"
                />
              </label>
            </div>
          </section>

          <aside className="h-fit rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5 sm:p-6 lg:sticky lg:top-32">
            <h2 className="text-lg font-black text-slate-900 dark:text-white">Review order</h2>
            <div className="mt-4 max-h-64 space-y-3 overflow-y-auto pr-1">
              {items.map((item) => (
                <div key={item.key} className="flex gap-3">
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800">
                    {item.image && <img src={item.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-slate-800 dark:text-slate-200">{item.name}</p>
                    <p className="text-xs text-slate-500">Qty {item.quantity}{item.variantName ? ` · ${item.variantName}` : ''}</p>
                  </div>
                  <GoldenAmount amount={item.priceGolden * item.quantity} size="sm" />
                </div>
              ))}
            </div>
            <div className="mt-5 border-t border-slate-200 dark:border-white/10 pt-4">
              <div className="flex justify-between text-sm text-slate-500">
                <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
                <span>Shipping included</span>
              </div>
              <div className="mt-3 flex items-end justify-between">
                <span className="font-bold text-slate-900 dark:text-white">Total</span>
                <GoldenAmount amount={totalGolden} size="lg" />
              </div>
              <div className="mt-4 rounded-xl bg-slate-50 dark:bg-white/5 p-3 text-sm">
                <div className="flex justify-between text-slate-500 dark:text-slate-400">
                  <span>Your balance</span>
                  <GoldenAmount amount={balance} size="sm" />
                </div>
                <div className="mt-2 flex justify-between font-bold text-slate-900 dark:text-white">
                  <span>After checkout</span>
                  <GoldenAmount amount={Math.max(0, balance - totalGolden)} size="sm" />
                </div>
              </div>
            </div>

            {shortfall > 0 && (
              <p className="mt-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-3 text-sm font-semibold text-red-700 dark:text-red-300">
                You need {shortfall.toLocaleString()} more golden ticket{shortfall === 1 ? '' : 's'}.
              </p>
            )}
            {error && (
              <p role="alert" className="mt-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-700 dark:text-red-300">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || shortfall > 0 || !requiredComplete}
              className="mt-5 w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-3.5 font-black text-slate-950 shadow-lg shadow-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Confirming order…' : `Pay ${totalGolden.toLocaleString()} golden`}
            </button>
            <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-400">
              By placing your order, you confirm the delivery information is correct.
            </p>
          </aside>
        </div>
      </form>
    </main>
  );
}
